import useApi from "@/hooks/useApi";
import { Fragment, useEffect, useEffectEvent, useRef, useState } from "react";
import { ChevronRightIcon, MenuIcon } from "lucide-react";
import { useSidebarStore } from "../Layout/Store";
import Avatar from "../Avatar";
import BoxProfile from "./Components/BoxProfile";
import Notification from "./Components/Notification";
import GlobalSearch from "./Components/GlobalSearch";
import { showErrorAlert, resolveAvatarUrl } from "@/Utils";
import { useLoggedUserStore } from "@/Store";

import S from "./styles.module.scss";

import { ISidebar } from "../../types/ISidebar";
import { useLocation } from "react-router-dom";

const Header = ({ sidebarData }: ISidebar) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const { getRequest } = useApi();
  const location = useLocation();
  const loggedUser = useLoggedUserStore((state) => state.loggedUser);
  const isAdmin = ["superadmin", "owner", "admin"].includes(
    loggedUser?.type ?? ""
  );

  const {
    handleOpenSidebar,
    boxProfileOpen,
    toggleBoxProfile,
    closeBoxProfile,
  } = useSidebarStore();
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const crumbs =
    sidebarData
      .map((item) => {
        if (item.submenu) {
          const subitem = item.submenu.find(
            (sub) => location.pathname === sub.path
          );
          return subitem ? [String(item.label), subitem.label] : null;
        }
        return location.pathname === item.path ? [String(item.label)] : null;
      })
      .find(Boolean) ?? [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      if (
        boxRef.current &&
        !boxRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        closeBoxProfile();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [closeBoxProfile]);

  const fetchUser = useEffectEvent(async () => {
    try {
      const response = await getRequest("/api/user");
      const data = await response.json();

      setName(data.name);
      setEmail(data.email);
    } catch (error) {
      showErrorAlert(error);
    }
  });

  useEffect(() => {
    void fetchUser();
  }, []);

  return (
    <header className={S["header-container"]}>
      <div className={S["menu-icon"]}>
        <MenuIcon className={S.icon} size={24} onClick={handleOpenSidebar} />
      </div>
      <h2 className={S.breadcrumb}>
        {crumbs.map((crumb, index) => {
          const isCurrent = index === crumbs.length - 1;
          return (
            <Fragment key={crumb}>
              {index > 0 && (
                <ChevronRightIcon
                  size={16}
                  className={S.crumbSeparator}
                  aria-hidden="true"
                />
              )}
              <span className={isCurrent ? S.crumbCurrent : S.crumbParent}>
                {crumb}
                {!isCurrent && <span className={S.srOnly}> / </span>}
              </span>
            </Fragment>
          );
        })}
      </h2>
      <div className={S["menu-right"]}>
        {isAdmin && <GlobalSearch />}
        <Notification />
        <button
          ref={buttonRef}
          type="button"
          className={boxProfileOpen ? S.avatarOpen : ""}
          onClick={toggleBoxProfile}
          aria-haspopup="true"
          aria-expanded={boxProfileOpen}
          aria-label="Menu da conta"
        >
          <Avatar size={32} src={resolveAvatarUrl(loggedUser?.image)} />
        </button>
      </div>
      {boxProfileOpen && (
        <BoxProfile
          ref={boxRef}
          name={name}
          email={email}
          image={loggedUser?.image}
        />
      )}
    </header>
  );
};

export default Header;

